import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Alert,
  Modal,
  InputLabel,
  Snackbar,
} from "@mui/material";

import { addWorkspace, putWorkspace } from "../../actions/workspaceAction";
import { Link, useNavigate } from "react-router-dom";
import ModalBox from "../../themes/overrides/ModalBox";
import OutlinedInput from "../../themes/overrides/OutlinedInput";
import AddButton from "../../themes/overrides/AddButton";
import OutlinedAddButton from "../../themes/overrides/OutlinedAddButton";
import ReplayIcon from "@mui/icons-material/Replay";

const WorkspaceModal = ({ isOpen, onClose, currentWorkspace, refreshList, isEdit = false }) => {
  const [workspace, setWorkspace] = useState({ name: "" });
  const error = useSelector((state) => state.workspace.error);
  const createError = useSelector((state) => state.workspace.createError);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState("success");
  const modalTitle = isEdit ? "Edit Workspace" : "Create Workspace";
  const buttonText = isEdit ? "Save Changes" : "Create New Workspace";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentWorkspace) {
      setWorkspace(currentWorkspace);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    if (isOpen && currentWorkspace?.data) {
      setWorkspace({ name: currentWorkspace.data.name });
    } else if (!isOpen) {
      setWorkspace({ name: "" });
    }
  }, [currentWorkspace, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      dispatch({ type: 'CLEAR_WORKSPACE_ERROR' });
      setWorkspace({ name: "" });
    }
  }, [isOpen, dispatch]);


  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      setSeverity("error");
      setOpenSnackbar(true);
    }
  }, [error]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setWorkspace((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (workspace.name.trim() == "") {
      dispatch({
        type: 'CREATE_WORKSPACE_FAIL',
        payload: "Workspace name cannot be empty or just spaces"
      });
      return; // Return to prevent submission
    }

    if (isEdit && currentWorkspace) {
      dispatch(putWorkspace(currentWorkspace.data._id, workspace.name, 'updateName'))
        .then(() => {
          setSuccessMessage("Workspace Updated");
          setOpenSnackbar(true);
          setSeverity("success");
          refreshList();
          onClose();
        })
    } else {
      dispatch(
        addWorkspace(workspace, navigate, (success) => {
          if (!success) return;

          setSuccessMessage("Workspace Created");
          setOpenSnackbar(true);
          setSeverity("success");
          setWorkspace({ name: "" });

          setTimeout(() => {
            refreshList();
            onClose();
            navigate("/space");
          }, 500);
        })
      );
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setErrorMessage("");
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <ModalBox component="form" sx={{
          width: { xs: '95%', sm: '400px', md: '500px' },
          maxHeight: { xs: '95vh', sm: '90vh' }, padding: { xs: 2, md: 4 }
        }} onSubmit={handleSubmit}>
          <Typography variant="h2">{modalTitle}</Typography>

          <Box sx={{ marginTop: 2 }}>
            <InputLabel shrink htmlFor="name">
              Workspace Name
            </InputLabel>
            <OutlinedInput
              fullWidth
              value={workspace.name}
              onChange={handleChange}
              name="name"
            />
            {createError && (
              <div className="mt-2 text-sm text-red-600">
                {createError}
              </div>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 2,
                marginBottom: 2,
              }}
            >
              <OutlinedAddButton
                label="Back"
                sx={{ mr: 1 }}
                component={Link}
                onClick={onClose}
                variant="outlined"
                color="primary"
                startIcon={<ReplayIcon />}
              />

              <AddButton
                label={buttonText}
                sx={{ mr: 1 }}
                type="submit"
              />
            </Box>
          </Box>
        </ModalBox>
      </Modal>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {severity === "success" ? successMessage : errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WorkspaceModal;
